<script setup lang="ts">
import { watchThrottled } from "@vueuse/core"

import archive1536 from "~/assets/images/files/archive/poly_archive@1536.webp"
import archive192 from "~/assets/images/files/archive/poly_archive@192.webp"
import archive512 from "~/assets/images/files/archive/poly_archive@512.webp"
import archive64 from "~/assets/images/files/archive/poly_archive@64.webp"
import albumIcon1536 from "~/assets/images/files/audio/poly_album@1536.webp"
import albumIcon192 from "~/assets/images/files/audio/poly_album@192.webp"
import albumIcon512 from "~/assets/images/files/audio/poly_album@512.webp"
import albumIcon64 from "~/assets/images/files/audio/poly_album@64.webp"
import folderIcon1536 from "~/assets/images/files/folder/poly_folder@1536.webp"
import folderIcon192 from "~/assets/images/files/folder/poly_folder@256.webp" // TODO
import folderIcon4608 from "~/assets/images/files/folder/poly_folder@4608.webp"
import folderIcon512 from "~/assets/images/files/folder/poly_folder@512.webp"
import folderIcon64 from "~/assets/images/files/folder/poly_folder@64.webp"
import home1536 from "~/assets/images/files/home/poly_home@1536.webp"
import home192 from "~/assets/images/files/home/poly_home@192.webp"
import home512 from "~/assets/images/files/home/poly_home@512.webp"
import home64 from "~/assets/images/files/home/poly_home@64.webp"
import { StaticFile } from "~/classes/file/StaticFile"
import { FileThumbnailKindBuf } from "~/classes/generated/file"
import { isSafari } from "~/utils/platform"

defineOptions({
  inheritAttrs: false,
})

// File thumbnail logic
//
// - find a candidate thumbnail in the file's properties
//     - if none exists, load the unknown-item icon
// - try and retrieve the thumbnail from our sandbox
// - if that fails and we're offline, show the broken image
//   and watch for when we come back online
// - otherwise if we're online, load the url of the thumbnail image
// - when the image is done loading, if it was loaded via url
//   save it in the sandbox
// - on resize event, check whether we should load a different
//   thumbnail

const props = defineProps({
  file: null,
  containerWidth: null,
  containerHeight: null,
  cropped: { type: Boolean },
  selected: { type: Boolean },
  ghosted: { type: Boolean },
  shadowed: { type: Boolean },
  flat: { type: Boolean },
  hideStale: { type: Boolean }
})

/**
 * We receive client events like resize, so we need to handle events with a state machine.
 * With images on the web, there is a delay between when the image element appears and
 * when the image is loaded. To avoid that, we hide the image element until the image is
 * "ready". However, there are times where we want to update the image (e.g. window
 * resizing) so we need a "busy" state for when the image is being loaded after already
 * having been loaded once.
 */
const state = ref("unset") as Ref<
  | "unset" // initial state
  | "not-ready" // initial set up tasks (loading sandbox data, downloading thumbs)
  | "broken" // image cannot be loaded from remote (e.g. offline)
  | "busy" // we are busy downloading the file from cache or server, skip updates
  | "free"
>

const _console = useLogger("file-thumbnail-image", theme.colors.pink.hex)

const { thumbnailPreviewImage } = useGlobals()
const { isFileFresh, isOffline } = useCache()
const { getOfflineFileThumbnails } = useSandbox()

const fileThumbnail = useTemplateRef("fileThumbnail")
const offlineThumbnails: Set<string> = new Set()
const fetchedThumbnail = ref<string>()

const folder = computed(() => props.file.isFolder())
const stale = computed(() => !props.hideStale && !isFileFresh(props.file))
const safari = await isSafari()

const imageDimensions = computed(() => {
  let width = props.file.mediaWidthPx() || props.file.mediaWidthCm() || props.containerWidth
  let height = props.file.mediaHeightPx() || props.file.mediaHeightCm() || props.containerHeight
  if (props.cropped || width === 0 || height === 0)
    return { width: props.containerWidth, height: props.containerHeight }

  if (height !== props.containerHeight || width !== props.containerWidth) {
    // if our ratio is less than the containers (width / height), we anchor the size to the
    // container height, and scale the width accordingly, and vice versa.
    const ratio = width / height
    const containerRatio = props.containerWidth / props.containerHeight
    if (ratio < containerRatio) {
      height = props.containerHeight
      width = height * ratio
    }
    else {
      width = props.containerWidth
      height = width / ratio
    }
  }
  return { width, height }
})

/** Updates the image file based on container size, loaded state of uri, etc. */
async function updateImage() {
  if (!fileThumbnail.value)
    return

  // if we are busy or not ready we are signaling that we are doing something
  if (["busy", "not-ready"].includes(state.value))
    return

  // if we have no thumbnails then we are either a non-image file or we're not ready
  // to load thumbnails. Let's check if we are a special file type
  if (!isDefined(props.file.thumbnailSet())) {
    let bestUri: string | undefined
    if (props.file.isUserHomeFolder())
      bestUri = getBestHomeUrl()
    else if (props.file.isUserArchiveFolder())
      bestUri = getBestArchiveUrl()
    else if (props.file.isFolder())
      bestUri = getBestFolderUrl()
    else if (props.file.isAudio())
      bestUri = getBestAudioUrl()

    if (isDefined(bestUri)) {
      if (fetchedThumbnail.value === bestUri)
        return
      fetchedThumbnail.value = bestUri
      // TODO: load the right file per size
      state.value = "not-ready"
      await setFileThumbnail(bestUri)
      state.value = "free"
      return
    }
  }

  // NOTE: we can avoid this delay if we don't want to be efficient
  // about using offline thumbnails, as we can just attempt to load the best
  // thumbnail and react if it fails. For now we get the offline thumbnails
  // in advance.
  if (["unset"].includes(state.value)) {
    // retrieve data from sandbox
    state.value = "not-ready"
    const thumbnails = await getOfflineFileThumbnails(props.file)
    for (const thumbnail of thumbnails)
      offlineThumbnails.add(thumbnail)
  }

  const bestUri = getBestImageUrl()
  if (!bestUri) {
    state.value = "broken"
    return
  }

  // This means we have no changes to make, return
  else if (state.value === "free" && fetchedThumbnail.value === bestUri) {
    return
  }

  // We have changes to make, go busy
  // NOTE: this only applies to already loaded images. If we are not already loaded,
  // we should remain "not-ready" so we don't flash in on load.
  else if (state.value === "free") {
    state.value = "busy"
  }

  // we have the thumbnail cached, serve it
  if (offlineThumbnails.has(bestUri)) {
    fetchedThumbnail.value = bestUri
    await loadOfflineThumbnailFromCache(bestUri)
    state.value = "free"
    return
  }

  // download the thumbnail and set it as the file image, and save it to the sandbox
  // afterward.
  fetchedThumbnail.value = bestUri
  try {
    const response = await downloadImageBuffer(bestUri)
    await setFileBlobThumbnail(response)
    state.value = "free"
    // await here so we hit the catch block.
    await saveThumbnailToSandbox(response)
  }
  catch {
    // if we already had a working image, let's just go back to it
    if (fileThumbnail.value!.getAttribute("src"))
      state.value = "free"
    else
      state.value = "broken"
    fetchedThumbnail.value = undefined
    return
  }
}

/**
 * returns the best image we could use to render the object, given the height
 * and width of the container, and the list of images that are saved offline,
 * and whether we are offline or not.
 */
function getBestImageUrl() {
  if (isOffline())
    return getBestOfflineImageUrl()
  const { file } = props
  const minWidth = Math.min(props.containerWidth, file.mediaWidthPx() || Number.MAX_SAFE_INTEGER)
  const minHeight = Math.min(props.containerHeight, file.mediaHeightPx() || Number.MAX_SAFE_INTEGER)
  const minDim = Math.max(minWidth, minHeight)
  const set = file.thumbnailSet()
  if (!set)
    return
  let best: FileThumbnail | undefined
  if (minDim >= 1536)
    best = set.images[FileThumbnailKindBuf.Native4608]
  if (minDim >= 512)
    best = set.images[FileThumbnailKindBuf.Native1536]
  if (minDim >= 192)
    best = best || set.images[FileThumbnailKindBuf.Native512]
  if (minDim >= 64)
    best = best || set.images[FileThumbnailKindBuf.Native192]
  best = best || set.images[FileThumbnailKindBuf.Native64]
  if (!best)
    return
  return best.uri
}

function getBestOfflineImageUrl() {
  const { file } = props
  const minWidth = Math.min(props.containerWidth, file.mediaWidthPx() || Number.MAX_SAFE_INTEGER)
  const minHeight = Math.min(props.containerHeight, file.mediaHeightPx() || Number.MAX_SAFE_INTEGER)
  const minDim = Math.max(minWidth, minHeight)
  const set = file.thumbnailSet()
  if (!set)
    return
  let best: FileThumbnail | undefined

  if (minDim >= 1536) {
    const newBest = set.images[FileThumbnailKindBuf.Native4608]
    if (newBest && offlineThumbnails.has(newBest?.uri))
      best = newBest
  }
  if (minDim >= 512) {
    const newBest = set.images[FileThumbnailKindBuf.Native1536]
    if (newBest && offlineThumbnails.has(newBest?.uri))
      best = newBest
  }
  if (minDim >= 192) {
    const newBest = best || set.images[FileThumbnailKindBuf.Native512]
    if (newBest && offlineThumbnails.has(newBest?.uri))
      best = newBest
  }
  if (minDim >= 64) {
    const newBest = best || set.images[FileThumbnailKindBuf.Native192]
    if (newBest && offlineThumbnails.has(newBest?.uri))
      best = newBest
  }
  const newBest = best || set.images[FileThumbnailKindBuf.Native64]
  if (newBest && offlineThumbnails.has(newBest?.uri))
    best = newBest
  if (!best)
    return
  return best.uri
}

/**
 * For now, we just assume a min pixel density of 2. In the future, we could
 * try and choose a size based on the user's screen properties
 */
function getBestFolderUrl() {
  const minWidth = Math.min(props.containerWidth, Number.MAX_SAFE_INTEGER)
  const minHeight = Math.min(props.containerHeight, Number.MAX_SAFE_INTEGER)
  const minDim = Math.max(minWidth, minHeight)
  if (minDim >= 1536)
    return folderIcon4608
  if (minDim >= 512)
    return folderIcon1536
  if (minDim >= 192)
    return folderIcon512
  if (minDim >= 64)
    return folderIcon192
  return folderIcon64
}

/**
 * For now, we just assume a min pixel density of 2. In the future, we could
 * try and choose a size based on the user's screen properties
 */
function getBestHomeUrl() {
  const minWidth = Math.min(props.containerWidth, Number.MAX_SAFE_INTEGER)
  const minHeight = Math.min(props.containerHeight, Number.MAX_SAFE_INTEGER)
  const minDim = Math.max(minWidth, minHeight)
  if (minDim >= 512)
    return home1536
  if (minDim >= 192)
    return home512
  if (minDim >= 64)
    return home192
  return home64
}

/**
 * For now, we just assume a min pixel density of 2. In the future, we could
 * try and choose a size based on the user's screen properties
 */
function getBestArchiveUrl() {
  const minWidth = Math.min(props.containerWidth, Number.MAX_SAFE_INTEGER)
  const minHeight = Math.min(props.containerHeight, Number.MAX_SAFE_INTEGER)
  const minDim = Math.max(minWidth, minHeight)
  if (minDim >= 512)
    return archive1536
  if (minDim >= 192)
    return archive512
  if (minDim >= 64)
    return archive192
  return archive64
}

/**
 * For now, we just assume a min pixel density of 2. In the future, we could
 * try and choose a size based on the user's screen properties
 */
function getBestAudioUrl() {
  const { file } = props
  const minWidth = Math.min(props.containerWidth, file.mediaWidthPx() || Number.MAX_SAFE_INTEGER)
  const minHeight = Math.min(props.containerHeight, file.mediaHeightPx() || Number.MAX_SAFE_INTEGER)
  const minDim = Math.max(minWidth, minHeight)
  if (minDim >= 512)
    return albumIcon1536
  if (minDim >= 192)
    return albumIcon512
  if (minDim >= 64)
    return albumIcon192
  return albumIcon64
}

async function downloadImageBuffer(uri: string): Promise<Blob> {
  const fetchUri = StaticFile.toThumbnailUrl(uri)
  const response = await fetch(fetchUri, {
    headers: new Headers({ Origin: location.origin }),
  })
  const blob = await response.blob()
  return blob
}

async function saveThumbnailToSandbox(imageBlob: Blob) {
  const { saveThumbnailToSandbox } = useSandbox()
  const thumbnail = fetchedThumbnail.value
  if (thumbnail && !offlineThumbnails.has(thumbnail)) {
    await saveThumbnailToSandbox(thumbnail, await imageBlob.arrayBuffer())
    offlineThumbnails.add(thumbnail)
  }
}

async function loadOfflineThumbnailFromCache(thumbnailUri: string) {
  if (isDesktop()) {
    // get the file as a local path
    const { getThumbnailWebFriendly } = useTauri()
    const uri = await getThumbnailWebFriendly(thumbnailUri)
    await setFileThumbnail(uri)
  }
  else {
    const { getCachedFileFromSandboxPath } = useSandbox()
    const file = await getCachedFileFromSandboxPath(thumbnailUri)
    await setFileBlobThumbnail(file)
  }
}

async function setFileThumbnail(url: string) {
  if (!fileThumbnail.value)
    return
  const { promise, resolve, reject } = Promise.withResolvers()
  fileThumbnail.value.onload = () => resolve(true)
  fileThumbnail.value.onerror = () => reject(new Error("Failed to load image"))
  fileThumbnail.value.src = url
  await promise
}

async function setFileBlobThumbnail(blob: WebFile | Blob) {
  if (!fileThumbnail.value)
    return
  // we don't need to set loading to true here, since we only care about the loading
  // pop-in effect on the _first_ load. Subsequent loads are correctly handled by the
  // browser, seemingly, so we can save ourselves the flash-in of the load.
  const blobUrl = URL.createObjectURL(blob)
  const { promise, resolve, reject } = Promise.withResolvers()
  fileThumbnail.value.onload = () => {
    URL.revokeObjectURL(blobUrl)
    resolve(true)
  }
  fileThumbnail.value.onerror = () => {
    URL.revokeObjectURL(blobUrl)
    reject(new Error("Failed to load image"))
  }
  fileThumbnail.value.src = blobUrl
  await promise
}

onMounted(async () => updateImage())
watchThrottled(() => props.containerWidth || props.containerHeight, () => updateImage(), { throttle: 1000 / 45 })
watch(() => props.file, () => updateImage())
</script>

<template>
  <div
    v-if="['unset', 'not-ready'].includes(state)"
    class="outer"
    v-bind="$attrs"
  />
  <div
    v-else-if="['broken'].includes(state)"
    class="outer"
    :style="{ width: px(containerWidth), height: px(containerHeight) }"
    v-bind="$attrs"
  >
    <file-thumbnail-blank
      file-type="unknown"
      :file
      :flat
      :container-width
      :container-height
      :class="{
        [thumbnailPreviewImage]: true,
        safari,
        selected,
        ghosted,
        stale,
        shadowed,
        flat,
        cropped,
      }"
    />
  </div>
  <div
    v-show="!['unset', 'not-ready', 'broken'].includes(state)"
    class="outer relative flex-none"
    v-bind="$attrs"
    :style="{
      width: px(imageDimensions.width),
      height: px(imageDimensions.height),
    }"
  >
    <file-thumbnail-pdf-curl
      v-if="!flat && !file.isBlank() && file.isPdf()"
      :container-width
      :container-height
      class="image-curl"
    />
    <img
      ref="fileThumbnail"
      :style="{ width: px(containerWidth), height: px(containerHeight) }"
      src=""
      draggable="false"
      class="thumbnail"
      :class="{
        [thumbnailPreviewImage]: true,
        safari,
        selected,
        ghosted,
        stale,
        shadowed,
        flat,
        folder,
        cropped,
      }"
    >
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.outer
  @apply flex items-center justify-center
  container-type: inline-size
.image-curl
  @apply absolute right-0 top-[0.1%] rounded-[#{$page-icon-br-thumbnail-md}] pointer-events-none select-none
  @apply z-2 // otherwise there's a bug with shadowing on the image
.thumbnail
  @apply object-center select-none max-h-full
  &:not(.flat)
    @apply rounded-[#{$page-icon-br-thumbnail-md}]

  &.cropped
    @apply object-cover aspect-square
  &:not(.cropped)
    @apply object-contain
  &.shadowed.safari:not(.folder)
    &.stale
      @apply outline outline-[#{$page-b-offline}] outline-0.75
    &.ghosted
      @apply outline outline-$global-blue-secondary outline-0.75
    &:hover
      @apply outline outline-$global-blue-primary outline-0.75
    &.selected
      @apply outline outline-$global-blue-primary outline-0.75

  &.shadowed:not(.safari), .shadowed.folder
    &.stale
      @include alphaShadow(var(--page-b-offline), 2px, drop-shadow(-4px 2px 4px #10101040))
    &.ghosted
      @include alphaShadow(var(--global-blue-secondary), 1px, drop-shadow(-4px 2px 4px #10101040))
    &:hover
      @include alphaShadow(var(--global-blue-primary), 1px, drop-shadow(-4px 2px 4px #10101040))
    &.selected
      @include alphaShadow(var(--global-blue-primary), 1px, drop-shadow(-4px 2px 4px #10101040))

.initialLoad
  @apply
.flat.folder
  @apply bg-$global-violet-accent

// Make the border radius smaller if we are at icon size
@container (width < 32px)
  .thumbnail:not(.flat)
    @apply rounded-[1px]
</style>
